import {
    BoolFilterInput,
    DatasetFilterInput, InfoBoolFilterOptions, InfoNumFilterOptions, InfoStringFilterOptions,
    NumFilterInput,
    StringFilterInput, VariantFormatFilterOptions, VariantFrequenciesFilterOptions, VariantInfoFilterOptions
} from '@/generated/graphql'
import {
    BoolFilterOptions, DatasetFilterOptions,
    NumberFilterValue,
    NumFilterOptions,
    StringFilterOptions
} from '@/components/pages/NewDatasetJobPage/types'

export const mapFilersToPhenoFilterInput = (
    selectedPhenoFilters: DatasetFilterOptions,
    selectedVariantFormatFilters: DatasetFilterOptions,
    selectedVariantFrequenciesFilters: DatasetFilterOptions,
    selectedVariantInfoFilters: DatasetFilterOptions
): DatasetFilterInput => {
    return {
        sample_filter: {
            pheno_filter: mapDatasetFilterOptions(selectedPhenoFilters)
        },
        variant_filter: {
            variant_format_filter: mapDatasetFilterOptions(selectedVariantFormatFilters),
            variant_frequencies_filter: mapDatasetFilterOptions(selectedVariantFrequenciesFilters),
            variant_info_filter: mapDatasetFilterOptions(selectedVariantInfoFilters)
        }
    }
}

export const mapVariantFiltersToFilterOptions = (filterOptions?: VariantFormatFilterOptions | VariantFrequenciesFilterOptions) => {
    return {
        bool_filters: mapStringArrayFiltersToBoolFilters(filterOptions?.bool_filters || []),
        num_filters: mapStringArrayFiltersToNumFilters(filterOptions?.num_filters || []),
        string_filters: mapStringArrayFiltersToStringFilters(filterOptions?.string_filters || [])
    }
}

export const mapVariantInfoFiltersToFilterOptions = (filterOptions?: VariantInfoFilterOptions) => {
    return {
        bool_filters: mapInfoFiltersToBoolFilters(filterOptions?.bool_filters || []),
        num_filters: mapInfoFiltersToNumFilters(filterOptions?.num_filters || []),
        string_filters: mapInfoFiltersToStringFilters(filterOptions?.string_filters || [])
    }
}

export const handleFractionNonMissing = (fractionNonMissing?: number) => {
    return fractionNonMissing ? ` ( ${fractionNonMissing} )` : ''
}

const mapDatasetFilterOptions = (filters: DatasetFilterOptions) => {
    return {
        string_filters: mapSelectedStringFiltersToFilterInput(filters.stringFilters),
        num_filters: mapSelectedNumFiltersToFilterInput(filters.numFilters),
        bool_filters: mapSelectedBoolFiltersToFilterInput(filters.boolFilters)
    }
}

const mapSelectedBoolFiltersToFilterInput = (selectedBoolFilters: Record<string, boolean>): BoolFilterInput[] => {
    const boolFilters: BoolFilterInput[] = []
    Object.keys(selectedBoolFilters).forEach(key => boolFilters.push({name: key, value: selectedBoolFilters[key]}))
    return boolFilters
}

const mapSelectedNumFiltersToFilterInput = (selectedNumFilters: Record<string, NumberFilterValue>): NumFilterInput[] => {
    const numFilters: NumFilterInput[] = []
    Object.keys(selectedNumFilters).forEach(key => {
        if(selectedNumFilters[key].min !== '' && selectedNumFilters[key].max !== '') {
            const max = Number(selectedNumFilters[key].max)
            const min = Number(selectedNumFilters[key].min)

            if(!Number.isNaN(max) || !Number.isNaN(min)) {
                numFilters.push({name: key, lower_or_equal_than: isNaN(max) ? null : max, greater_or_equal_than: isNaN(min) ? null : min})
            } else {
                console.warn('Selected number input has invalid values and will not be used for filtering!!!', min, max)
            }
        } else {
            console.warn('Selected number input has empty values and will not be used for filtering!!!')
        }
    })
    return numFilters
}

const mapSelectedStringFiltersToFilterInput = (selectedStringFilters: Record<string, string[]>): StringFilterInput[] => {
    const stringFilters: StringFilterInput[] = []
    Object.keys(selectedStringFilters).forEach(key => stringFilters.push({name: key, values: selectedStringFilters[key]}))
    return stringFilters
}

const mapStringArrayFiltersToBoolFilters = (boolFilters: string[]): BoolFilterOptions[] => {
    return boolFilters?.map(filterName => ({name: filterName}))
}

const mapStringArrayFiltersToNumFilters = (numFilters: string[]): NumFilterOptions[] => {
    return numFilters?.map(filterName => ({
        name: filterName,
        min_value: Number.MIN_VALUE,
        max_value: Number.MAX_VALUE,
        freeText: true
    }))
}

const mapStringArrayFiltersToStringFilters = (stringFilters: string[]): StringFilterOptions[] => {
    return stringFilters?.map(filterName => ({name: filterName, freeText: true}))
}

const mapInfoFiltersToBoolFilters = (boolFilters: InfoBoolFilterOptions[]): BoolFilterOptions[] => {
    return boolFilters?.map(filter => ({
        name: filter.name,
        fraction_non_missing: filter.fraction_non_missing
    }))
}

const mapInfoFiltersToNumFilters = (numFilters: InfoNumFilterOptions[]): NumFilterOptions[] => {
    return numFilters?.map(filter => ({
        name: filter.name,
        min_value: isEmptyValue(filter.min_value) ? Number.MIN_VALUE : filter.min_value,
        max_value: isEmptyValue(filter.max_value) ? Number.MAX_VALUE : filter.max_value,
        fraction_non_missing: filter.fraction_non_missing,
        freeText: isEmptyValue(filter.min_value) || isEmptyValue(filter.max_value)
    }))
}

const mapInfoFiltersToStringFilters = (stringFilters: InfoStringFilterOptions[]): StringFilterOptions[] => {
    return stringFilters?.map(filter => ({
        name: filter.name,
        fraction_non_missing: filter.fraction_non_missing,
        freeText: true
    }))
}

const isEmptyValue = (value: number | null | undefined): boolean => {
    return value === null || value === undefined
}